import axios from 'axios';
import {StakeholderMapResponse} from "./StakeholderMapResponse.ts";
import {StakeholderPostRequest} from "./StakeholderPostRequest.ts";
import {StakeholderApiResponse, CategoryApiResponse} from "./StakeholderMapApiResponse.ts";
import { CategoryData } from '../../../stakeholdersMap/types/CategoryData.ts';
import { CreateCategoryData } from '../../../stakeholdersMap/types/CreateCategoryData.ts';

// Create axios instance with credentials enabled
const apiClient = axios.create({
    withCredentials: true,
});

export class StakeholderMapRequest {
    private readonly baseUrl: string;
    
    constructor() {
        this.baseUrl =  import.meta.env.VITE_API_BASE_URL;
    }

    /**
     * Fetches stakeholder map data by ID
     * @param id - The ID of the stakeholder map to fetch
     * @returns Promise with the stakeholder map data (categories with their stakeholders)
     * @throws Error if the request fails
     */
    public async getStakeholderMap(id: string): Promise<StakeholderMapResponse> {
        try {
            const response = await apiClient.get<StakeholderMapResponse>(
                `${this.baseUrl}/stakeholderMaps/${id}`
            );
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(`Failed to fetch stakeholder map: ${error.message}`);
            }
            throw error;
        }
    }

    /**
     * Creates a new stakeholder map
     * @param stakeholderMapData - The stakeholder map data to create
     * @returns Promise with the created stakeholder map data
     * @throws Error if the request fails
     */
    public async createStakeholderMap(): Promise<StakeholderMapResponse> {
        try {
            const response = await apiClient.post<StakeholderMapResponse>(
                `${this.baseUrl}/stakeholderMaps`,
                {}
            );
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(`Failed to create stakeholder map: ${error.message}`);
            }
            throw error;
        }
    }

    /**
     * Adds a new stakeholder to an existing stakeholder map
     * @param stakeholderMapId - The ID of the stakeholder map to add the stakeholder to
     * @param data - The stakeholder data and category ID
     * @returns Promise with the created stakeholder ID
     * @throws Error if the request fails
     */
    public async postStakeholder(stakeholderMapId: string, data: StakeholderPostRequest): Promise<StakeholderApiResponse> {
        try {
            const response = await apiClient.post<StakeholderApiResponse>(
                `${this.baseUrl}/stakeholderMaps/${stakeholderMapId}/stakeholders`,
                data
            );
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(`Failed to add stakeholder to map: ${error.message}`);
            }
            throw error;
        }
    }

    /**
     * Updates an existing stakeholder in a stakeholder map
     * @param stakeholderMapId - The ID of the stakeholder map containing the stakeholder
     * @param stakeholderId - The ID of the stakeholder to update
     * @param data - The updated stakeholder data and category ID
     * @returns Promise with the updated stakeholder ID
     * @throws Error if the request fails
     */
    public async updateStakeholder(stakeholderMapId: string, stakeholderId: string, data: StakeholderPostRequest): Promise<StakeholderApiResponse> {
        try {
            const response = await apiClient.put<StakeholderApiResponse>(
                `${this.baseUrl}/stakeholderMaps/${stakeholderMapId}/stakeholders/${stakeholderId}`,
                data
            );
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(`Failed to update stakeholder: ${error.message}`);
            }
            throw error;
        }
    }

    /**
     * Deletes a stakeholder from a stakeholder map
     * @param stakeholderMapId - The ID of the stakeholder map containing the stakeholder
     * @param stakeholderId - The ID of the stakeholder to delete
     * @returns Promise with the deleted stakeholder ID
     * @throws Error if the request fails
     */
    public async deleteStakeholder(stakeholderMapId: string, stakeholderId: string): Promise<StakeholderApiResponse> {
        try {
            const response = await apiClient.delete<StakeholderApiResponse>(
                `${this.baseUrl}/stakeholderMaps/${stakeholderMapId}/stakeholders/${stakeholderId}`
            );
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(`Failed to delete stakeholder: ${error.message}`);
            }
            throw error;
        }
    }

    /**
     * Adds a new category to an existing stakeholder map
     * @param stakeholderMapId - The ID of the stakeholder map to add the category to
     * @param data - The category data (name)
     * @returns Promise with the created category ID
     * @throws Error if the request fails
     */
    public async postCategory(stakeholderMapId: string, data: CreateCategoryData): Promise<CategoryApiResponse> {
        try {
            const response = await apiClient.post<CategoryApiResponse>(
                `${this.baseUrl}/stakeholderMaps/${stakeholderMapId}/categories`,
                data
            );
            console.log(response);
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(`Failed to add category to map: ${error.message}`);
            }
            throw error;
        }
    }

    /**
     * Updates an existing category in a stakeholder map
     * @param stakeholderMapId - The ID of the stakeholder map containing the category
     * @param categoryId - The ID of the category to update
     * @param data - The updated category data (name)
     * @returns Promise with the updated category ID
     * @throws Error if the request fails
     */
    public async updateCategory(stakeholderMapId: string, categoryId: string, data: CategoryData): Promise<CategoryApiResponse> {
        try {
            const response = await apiClient.put<CategoryApiResponse>(
                `${this.baseUrl}/stakeholderMaps/${stakeholderMapId}/categories/${categoryId}`,
                data
            );
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(`Failed to update category: ${error.message}`);
            }
            throw error;
        }
    }

    /**
     * Deletes a category from a stakeholder map
     * @param stakeholderMapId - The ID of the stakeholder map containing the category
     * @param categoryId - The ID of the category to delete
     * @returns Promise with the deleted category ID
     * @throws Error if the request fails
     */
    public async deleteCategory(stakeholderMapId: string, categoryId: string): Promise<CategoryApiResponse> {
        try {
            const response = await apiClient.delete<CategoryApiResponse>(
                `${this.baseUrl}/stakeholderMaps/${stakeholderMapId}/categories/${categoryId}`
            );
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(`Failed to delete category: ${error.message}`);
            }
            throw error;
        }
    }

    /**
     * Gets all stakeholders
     * @returns Promise with the array of stakeholders
     * @throws Error if the request fails
     */
    public async getAllStakeholders(): Promise<StakeholderMapResponse[]> {
        try {
            const response = await apiClient.get<StakeholderMapResponse[]>(
                `${this.baseUrl}/stakeholderMaps`
            );
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(`Failed to fetch stakeholders: ${error.message}`);
            }
            throw error;
        }
    }
}

// Create a singleton instance
export const stakeholderMapRequest = new StakeholderMapRequest();